import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, Marker, Polyline, useLoadScript } from '@react-google-maps/api';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  SkipNext as NextIcon,
  SkipPrevious as PrevIcon,
} from '@mui/icons-material';
import { formatDeviceLabel, getDeviceDisplayName, getEffectiveGps } from '../utils/deviceDisplayUtils';
import {
  DEFAULT_PARENT_MOVE_LIMIT,
  PARENT_MOVE_LIMITS,
  curvedPath,
  moveBends,
  moveSerials,
  parentMoves,
} from '../utils/parentMoves';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDBzNIMvmobitJ-vi8Z-kzVfK99qSb7jRw';
const GOOGLE_MAPS_LIBRARIES = ['geometry', 'marker'];
const MOVE_COLOR = '#7b1fa2';
const DEVICE_COLOR = '#ed6c02';
const PLAY_STEP_MS = 1200;

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const shortLabel = (device, serial) => {
  const name = device ? getDeviceDisplayName(device) : '';
  return name && name.toLowerCase() !== serial ? name : serial.slice(-6);
};

const DiagnosticsParentMoveMap = ({ open, onClose, serial, events, devicesBySerial, initialStep }) => {
  const theme = useTheme();
  const mapRef = useRef(null);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  const [limit, setLimit] = useState(DEFAULT_PARENT_MOVE_LIMIT);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);

  const moves = useMemo(() => parentMoves(events, limit), [events, limit]);
  const bends = useMemo(() => moveBends(moves), [moves]);
  const device = devicesBySerial[serial];

  const gpsOf = useCallback((s) => {
    const gps = getEffectiveGps(devicesBySerial[s]);
    return gps ? { lat: gps.lat, lng: gps.lng } : null;
  }, [devicesBySerial]);

  useEffect(() => {
    if (!open) return;
    setPlaying(false);
    setStep(initialStep && initialStep <= moves.length ? initialStep : moves.length);
  }, [open, initialStep, moves.length]);

  useEffect(() => {
    if (!playing) return undefined;
    if (step >= moves.length) {
      setPlaying(false);
      return undefined;
    }
    const id = setTimeout(() => setStep((s) => s + 1), PLAY_STEP_MS);
    return () => clearTimeout(id);
  }, [playing, step, moves.length]);

  const parents = useMemo(() => moveSerials(moves).map((s) => ({ serial: s, gps: gpsOf(s), device: devicesBySerial[s] })), [moves, gpsOf, devicesBySerial]);
  const deviceGps = gpsOf(serial);
  const currentParent = step > 0 ? moves[step - 1]?.to : moves[0]?.from;

  const involved = useMemo(() => new Set([serial, ...parents.map((p) => p.serial)]), [serial, parents]);
  const context = useMemo(() => Object.entries(devicesBySerial)
    .filter(([s]) => !involved.has(s))
    .map(([s, d]) => ({ serial: s, gps: getEffectiveGps(d) }))
    .filter((c) => c.gps), [devicesBySerial, involved]);

  const fitToMoves = useCallback(() => {
    const map = mapRef.current;
    if (!map || !window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    let n = 0;
    [...parents.map((p) => p.gps), deviceGps].forEach((g) => {
      if (g) {
        bounds.extend(g);
        n += 1;
      }
    });
    if (n > 1) map.fitBounds(bounds, 80);
    else if (n === 1) {
      map.setCenter(bounds.getCenter());
      map.setZoom(18);
    }
  }, [parents, deviceGps]);

  useEffect(() => { if (isLoaded) fitToMoves(); }, [isLoaded, fitToMoves]);

  const colorScheme = isLoaded && window.google?.maps?.ColorScheme
    ? window.google.maps.ColorScheme[theme.palette.mode === 'dark' ? 'DARK' : 'LIGHT']
    : undefined;
  const missingGps = parents.filter((p) => !p.gps).length;

  const renderMap = () => {
    if (loadError) return <Alert severity="error">Failed to load Google Maps.</Alert>;
    if (!isLoaded) {
      return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><CircularProgress /></Box>;
    }
    const { SymbolPath } = window.google.maps;
    return (
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={deviceGps || parents.find((p) => p.gps)?.gps || { lat: 0, lng: 0 }}
        zoom={17}
        onLoad={(map) => { mapRef.current = map; fitToMoves(); }}
        options={{ streetViewControl: false, mapTypeControl: true, fullscreenControl: true, mapId: 'DIAG_PARENT_MOVES', colorScheme }}
      >
        {context.map((c) => (
          <Marker
            key={`ctx-${c.serial}`}
            position={{ lat: c.gps.lat, lng: c.gps.lng }}
            clickable={false}
            icon={{ path: SymbolPath.CIRCLE, scale: 3, fillColor: '#9e9e9e', fillOpacity: 0.45, strokeWeight: 0 }}
          />
        ))}

        {moves.map((m, i) => {
          const a = gpsOf(m.from);
          const b = gpsOf(m.to);
          if (!a || !b || m.n > step) return null;
          const isCurrent = m.n === step;
          const age = step > 1 ? (m.n - 1) / (step - 1) : 1;
          const opacity = isCurrent ? 1 : 0.25 + 0.45 * age;
          const { points, mid } = curvedPath(a, b, bends[i]);
          return (
            <React.Fragment key={`move-${m.n}`}>
              <Polyline
                path={points}
                options={{
                  strokeColor: MOVE_COLOR,
                  strokeOpacity: opacity,
                  strokeWeight: isCurrent ? 5 : 3,
                  zIndex: isCurrent ? 20 : 10,
                  icons: [{
                    icon: { path: SymbolPath.FORWARD_CLOSED_ARROW, scale: isCurrent ? 3.5 : 2.5, fillColor: MOVE_COLOR, fillOpacity: opacity, strokeColor: MOVE_COLOR, strokeOpacity: opacity, strokeWeight: 1 },
                    offset: '88%',
                  }],
                }}
                onClick={() => { setPlaying(false); setStep(m.n); }}
              />
              <Marker
                position={mid}
                zIndex={isCurrent ? 40 : 30}
                onClick={() => { setPlaying(false); setStep(m.n); }}
                title={`${m.n}. ${fmtTime(m.at)}`}
                label={{ text: String(m.n), color: '#fff', fontSize: '11px', fontWeight: '700' }}
                icon={{ path: SymbolPath.CIRCLE, scale: isCurrent ? 11 : 9, fillColor: MOVE_COLOR, fillOpacity: isCurrent ? 1 : 0.85, strokeColor: '#fff', strokeWeight: 2 }}
              />
            </React.Fragment>
          );
        })}

        {deviceGps && currentParent && gpsOf(currentParent) && (
          <Polyline
            path={[deviceGps, gpsOf(currentParent)]}
            options={{
              strokeOpacity: 0,
              zIndex: 5,
              icons: [{ icon: { path: 'M 0,-1 0,1', strokeColor: DEVICE_COLOR, strokeOpacity: 0.9, scale: 2.5 }, offset: '0', repeat: '10px' }],
            }}
          />
        )}

        {parents.filter((p) => p.gps).map((p) => {
          const isCurrent = p.serial === currentParent;
          return (
            <Marker
              key={`ap-${p.serial}`}
              position={p.gps}
              zIndex={isCurrent ? 50 : 45}
              title={p.device ? formatDeviceLabel(p.device) : p.serial}
              label={{ text: shortLabel(p.device, p.serial), color: theme.palette.text.primary, fontSize: '11px', fontWeight: isCurrent ? '700' : '500' }}
              icon={{
                path: SymbolPath.CIRCLE,
                scale: 9,
                fillColor: isCurrent ? MOVE_COLOR : theme.palette.background.paper,
                fillOpacity: 1,
                strokeColor: MOVE_COLOR,
                strokeWeight: 2.5,
                labelOrigin: new window.google.maps.Point(0, 2.6),
              }}
            />
          );
        })}

        {deviceGps && (
          <Marker
            position={deviceGps}
            zIndex={60}
            title={device ? formatDeviceLabel(device) : serial}
            icon={{ path: SymbolPath.CIRCLE, scale: 7, fillColor: DEVICE_COLOR, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 }}
          />
        )}
      </GoogleMap>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" noWrap>Parent changes · {device ? formatDeviceLabel(device) : serial}</Typography>
          <Typography variant="caption" color="text.secondary">
            {moves.length ? `${fmtTime(moves[0].at)} – ${fmtTime(moves[moves.length - 1].at)}` : 'No parent changes in this window'}
          </Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={limit} onChange={(_, v) => { if (v) setLimit(v); }}>
          {PARENT_MOVE_LIMITS.map((n) => <ToggleButton key={n} value={n}>{`Last ${n}`}</ToggleButton>)}
        </ToggleButtonGroup>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', gap: 2, height: 560, pb: 2 }}>
        <Box sx={{ flex: 1, position: 'relative', borderRadius: 1, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
          {renderMap()}
          <Box sx={{ position: 'absolute', left: 10, bottom: 10, bgcolor: 'background.paper', boxShadow: 2, borderRadius: 1, px: 1, py: 0.5, display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: DEVICE_COLOR }} />
              <Typography variant="caption">This device</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', border: `2px solid ${MOVE_COLOR}` }} />
              <Typography variant="caption">Parent</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: MOVE_COLOR }} />
              <Typography variant="caption">Parent at this step</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Previous"><span><IconButton size="small" disabled={step <= 1} onClick={() => { setPlaying(false); setStep((s) => s - 1); }}><PrevIcon /></IconButton></span></Tooltip>
            <Tooltip title={playing ? 'Pause' : 'Play from the start'}>
              <span>
                <IconButton
                  size="small"
                  disabled={!moves.length}
                  onClick={() => {
                    if (playing) setPlaying(false);
                    else {
                      if (step >= moves.length) setStep(0);
                      setPlaying(true);
                    }
                  }}
                >
                  {playing ? <PauseIcon /> : <PlayIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Next"><span><IconButton size="small" disabled={step >= moves.length} onClick={() => { setPlaying(false); setStep((s) => s + 1); }}><NextIcon /></IconButton></span></Tooltip>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{`${step} / ${moves.length}`}</Typography>
          </Box>
          <Slider
            size="small"
            min={0}
            max={Math.max(moves.length, 1)}
            step={1}
            value={step}
            disabled={!moves.length}
            onChange={(_, v) => { setPlaying(false); setStep(v); }}
            sx={{ color: MOVE_COLOR, mx: 1, width: 'auto' }}
          />
          {missingGps > 0 && (
            <Typography variant="caption" color="warning.main" sx={{ mb: 1 }}>
              {`${missingGps} parent${missingGps === 1 ? ' has' : 's have'} no location; moves to or from ${missingGps === 1 ? 'it' : 'them'} are listed but not drawn.`}
            </Typography>
          )}
          <List dense sx={{ overflowY: 'auto', flex: 1 }}>
            {moves.map((m) => {
              const drawn = gpsOf(m.from) && gpsOf(m.to);
              return (
                <ListItemButton
                  key={m.n}
                  selected={m.n === step}
                  onClick={() => { setPlaying(false); setStep(m.n); }}
                  sx={{ opacity: m.n > step ? 0.45 : 1, borderRadius: 1, alignItems: 'flex-start' }}
                >
                  <Box sx={{ minWidth: 24, height: 24, mr: 1, mt: 0.25, borderRadius: '50%', bgcolor: MOVE_COLOR, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                    {m.n}
                  </Box>
                  <ListItemText
                    primary={`${shortLabel(devicesBySerial[m.from], m.from)} → ${shortLabel(devicesBySerial[m.to], m.to)}`}
                    secondary={`${fmtTime(m.at)}${drawn ? '' : ' · no location'}`}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DiagnosticsParentMoveMap;
